/**
 * tele_frontend_2/src/hooks/useGoogleAuth.js
 *
 * Custom hook to manage Google OAuth popup flow and token revocation.
 *
 * Exports:
 *   - useGoogleAuth({ onSuccess, onError, postMessageOrigins })
 *
 * Responsibilities:
 *  - Request an authorization URL from backend via getAuthSetupUrl()
 *  - Ensure the returned `authUrl` will redirect back to the frontend callback:
 *      `${window.location.origin}/google-auth-callback`
 *    (overrides any redirect_uri in the backend-provided URL)
 *  - Open a centered popup to the adjusted authUrl
 *  - Listen for postMessage events from the popup callback page (types: GOOGLE_AUTH_CODE, GOOGLE_AUTH_ERROR)
 *  - Forward the received code to backend via sendAuthCode(code, state)
 *  - Provide revokeAuth() helper (calls revokeAuthToken)
 *
 * Notes:
 *  - The hook attempts to be robust in rewriting the redirect_uri param:
 *    - Parses absolute URLs
 *    - Handles relative URLs using window.location.origin as base
 *    - Falls back to a manual query-string rewrite if URL parsing fails
 *  - The hook also adds a `postOrigin` query param containing `window.location.origin`
 *    which the popup callback page can use to choose a safe postMessage target origin.
 *
 * Security:
 *  - The hook maintains a list of allowed origins (from REACT_APP_ORIGIN, explicit param,
 *    and development localhost) and rejects messages from other origins.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useToast } from "components-lib";
import {
  getAuthSetupUrl,
  revokeAuthToken,
} from "api/googleAuthAPI";

const DEFAULT_POPUP_OPTIONS =
  "width=600,height=700,menubar=no,toolbar=no,resizable=yes,scrollbars=yes";

/**
 * Parse a comma-separated env value into origins array.
 */
function parseAllowedOrigins(envValue) {
  if (!envValue) return [];
  return envValue
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * Returns true if origin is allowed.
 * If allowed contains '*' allow any origin.
 */
function isOriginAllowed(origin, allowedOrigins) {
  if (!origin) return false;
  if (!allowedOrigins || allowedOrigins.length === 0) return false;
  if (allowedOrigins.includes("*")) return true;
  return allowedOrigins.includes(origin);
}

/**
 * Build centered popup window options.
 */
function buildPopupOptions(width = 600, height = 700) {
  const left =
    window.screenX + Math.max(0, Math.floor((window.outerWidth - width) / 2));
  const top =
    window.screenY + Math.max(0, Math.floor((window.outerHeight - height) / 2));
  return `width=${width},height=${height},left=${left},top=${top},menubar=no,toolbar=no,resizable=yes,scrollbars=yes`;
}

/**
 * Replace or inject redirect_uri and postOrigin query params into authUrl.
 *
 * Accepts absolute and relative authUrl values. If authUrl is relative, uses
 * window.location.origin as base for URL parsing.
 *
 * Returns adjusted auth URL string.
 */


/**
 * Hook: useGoogleAuth
 */
export const useGoogleAuth = ({
  onSuccess,
  onError,
  postMessageOrigins,
} = {}) => {
  const [isAuthLoading, setIsAuthLoading] = useState(false);
  const toast = useToast();
  const popupRef = useRef(null);
  const popupTimeoutRef = useRef(null);
  const mountedRef = useRef(true);

  // Allowed origins list - priority:
  // explicit param -> REACT_APP_ORIGIN env -> default dev origin
  const envOrigins = parseAllowedOrigins(import.meta.env.VITE_APP_ORIGIN || "");
  const explicitOrigins = Array.isArray(postMessageOrigins)
    ? postMessageOrigins
    : typeof postMessageOrigins === "string" && postMessageOrigins.length
      ? parseAllowedOrigins(postMessageOrigins)
      : [];
  const allowedOrigins = Array.from(
    new Set([...explicitOrigins, ...envOrigins]),
  );

  // Always allow localhost in dev
  if (
    import.meta.env.MODE !== "production" &&
    !allowedOrigins.includes("http://localhost:3000")
  ) {
    allowedOrigins.push("http://localhost:3000");
  }

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  /**
   * Close popup and clear timeouts.
   */
  const closePopup = useCallback(() => {
    try {
      if (popupRef.current && typeof popupRef.current.close === "function") {
        popupRef.current.close();
      }
    } catch (e) {
      // ignore cross-origin closing errors
    } finally {
      popupRef.current = null;
      if (popupTimeoutRef.current) {
        clearTimeout(popupTimeoutRef.current);
        popupTimeoutRef.current = null;
      }
    }
  }, []);

  /**
   * Message event handler - listens for messages from popup callback page.
   * Expects messages of shape:
   *  { type: 'GOOGLE_AUTH_CODE', payload: { code, state, ... } }
   *  { type: 'GOOGLE_AUTH_ERROR', payload: { error, state } }
   */
  // useEffect(() => {
  //   const handleMessage = async (event) => {
  //     // Basic validation
  //     if (
  //       allowedOrigins.length > 0 &&
  //       !isOriginAllowed(event.origin, allowedOrigins)
  //     ) {
  //       // If the origin isn't explicitly allowed, ignore the message.
  //       return;
  //     }

  //     if (!event.data || typeof event.data !== "object") return;
  //     const { type, payload } = event.data;

  //     if (type === "GOOGLE_AUTH_CODE") {
  //       const code = payload?.code;
  //       const state = payload?.state;
  //       const emailFromPayload = payload?.email || payload?.clinicEmail || payload?.accountEmail || null;
  //       if (!code) {
  //         toast({
  //           title: "Authentication failed",
  //           description: "No authorization code was returned.",
  //           status: "error",
  //           duration: 6000,
  //           isClosable: true,
  //         });
  //         if (onError) onError(new Error("no_code_returned"));
  //         closePopup();
  //         return;
  //       }

  //       setIsAuthLoading(true);
  //       try {
  //         // sendAuthCode is expected to call backend to exchange and persist tokens
  //         // Include email and clinicId if provided by the callback/popup
  //         const clinicIdFromPayload = payload?.clinicId || null;
  //         const resp = await sendAuthCode(code, state, emailFromPayload, clinicIdFromPayload);
  //         toast({
  //           title: "Connected",
  //           description: "Google Calendar connected successfully.",
  //           status: "success",
  //           duration: 4000,
  //           isClosable: true,
  //         });
  //         // update localStorage pending marker
  //         try {
  //           const p = JSON.parse(localStorage.getItem("google_oauth_pending") || "null");
  //           if (p) {
  //             p.status = "connected";
  //             p.ts = Date.now();
  //             p.backend = resp?.data || null;
  //             localStorage.setItem("google_oauth_pending", JSON.stringify(p));
  //           }
  //         } catch (e) {}
  //         if (onSuccess) onSuccess(resp);
  //       } catch (err) {
  //         const message =
  //           err?.data?.message ||
  //           err?.message ||
  //           "Could not complete authentication.";
  //         toast({
  //           title: "Authentication error",
  //           description: message,
  //           status: "error",
  //           duration: 6000,
  //           isClosable: true,
  //         });
  //         // mark pending failed
  //         try {
  //           const p = JSON.parse(localStorage.getItem("google_oauth_pending") || "null");
  //           if (p) {
  //             p.status = "error";
  //             p.error = message;
  //             p.ts = Date.now();
  //             localStorage.setItem("google_oauth_pending", JSON.stringify(p));
  //           }
  //         } catch (e) {}
  //         if (onError) onError(err);
  //       } finally {
  //         if (mountedRef.current) setIsAuthLoading(false);
  //         closePopup();
  //       }
  //     } else if (type === "GOOGLE_AUTH_ERROR") {
  //       const errMsg =
  //         payload?.error || "Authorization failed or was cancelled.";
  //       toast({
  //         title: "Authorization cancelled",
  //         description: errMsg,
  //         status: "warning",
  //         duration: 5000,
  //         isClosable: true,
  //       });
  //       if (onError) onError(new Error(errMsg));
  //       closePopup();
  //     }
  //   };

  //   window.addEventListener("message", handleMessage, false);
  //   return () => {
  //     window.removeEventListener("message", handleMessage, false);
  //   };
  // }, [allowedOrigins, toast, onSuccess, onError, closePopup]);

  /**
   * initiateAuth: begin OAuth flow
   * - obtains authUrl from backend
   * - rewrites redirect_uri to frontend callback to ensure popup/static callback receives the code
   * - opens popup and sets a timeout in case popup doesn't respond
   */
  const initiateAuth = useCallback(
    async (opts = {}) => {
      setIsAuthLoading(true);
      try {
        // opts should include email (required) and optionally clinicId
        const email = opts.email || null;
        const clinicId = opts.clinicId || null;

        if (!email) {
          throw new Error("Clinic email is required to initiate Google auth.");
        }

        // Persist pending auth info to localStorage so callback can read it
        try {
          const pending = {
            clinicId: clinicId || null,
            email: email,
            status: "pending",
            ts: Date.now(),
          };
          localStorage.setItem("google_oauth_pending", JSON.stringify(pending));
        } catch (e) {
          // ignore storage errors
        }

        // Request an auth URL from backend and pass email/clinicId so backend
        // can include appropriate state/login_hint.
        const res = await getAuthSetupUrl({postOrigin: window.location.origin });
        let authUrl = res?.data?.authUrl || res?.data?.authurl || null;
        if (!authUrl) {
          throw new Error("Authorization URL not returned by server.");
        }

        // Open popup
        const popupOptions =
          opts.popupOptions ||
          buildPopupOptions(600, 700) ||
          DEFAULT_POPUP_OPTIONS;
        popupRef.current = window.open(
          authUrl,
          "Google Authorization",
          popupOptions,
        );

        if (!popupRef.current) {
          // clear pending flag
          try {
            localStorage.removeItem("google_oauth_pending");
          } catch (e) {}
          throw new Error("Popup blocked. Please allow popups and try again.");
        }

        // Timeout fallback: if no response from popup within TIMEOUT_MS, consider it failed.
        const TIMEOUT_MS = opts.timeoutMs || 2 * 60 * 1000;
        if (popupTimeoutRef.current) {
          clearTimeout(popupTimeoutRef.current);
          popupTimeoutRef.current = null;
        }
        popupTimeoutRef.current = setTimeout(() => {
          popupTimeoutRef.current = null;
          popupRef.current = null;
          if (mountedRef.current) setIsAuthLoading(false);
          toast({
            title: "Authorization timed out",
            description:
              "No response from authorization popup. It may have been closed.",
            status: "info",
            duration: 4000,
            isClosable: true,
          });
          // mark pending as failed
          try {
            const p = JSON.parse(localStorage.getItem("google_oauth_pending") || "null");
            if (p) {
              p.status = "timeout";
              p.ts = Date.now();
              localStorage.setItem("google_oauth_pending", JSON.stringify(p));
            }
          } catch (e) {}
          if (onError) onError(new Error("popup_timeout"));
        }, TIMEOUT_MS);
      } catch (err) {
        const message =
          err?.data?.message ||
          err?.message ||
          "Could not initiate authorization.";
        toast({
          title: "Error",
          description: message,
          status: "error",
          duration: 6000,
          isClosable: true,
        });
        if (onError) onError(err);
        if (mountedRef.current) setIsAuthLoading(false);
      }
    },
    [onError, toast],
  );

  /**
   * revokeAuth - wrapper around revokeAuthToken for disconnecting Google account.
   */
  const revokeAuth = useCallback(async () => {
    setIsAuthLoading(true);
    try {
      const res = await revokeAuthToken();
      const msg = res?.data?.message || "Disconnected";
      toast({
        title: "Disconnected",
        description: msg,
        status: "info",
        duration: 4000,
        isClosable: true,
      });
    } catch (err) {
      const message =
        err?.data?.message || err?.message || "Could not disconnect.";
      toast({
        title: "Error",
        description: message,
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    } finally {
      if (mountedRef.current) setIsAuthLoading(false);
    }
  }, [toast]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      try {
        if (popupTimeoutRef.current) {
          clearTimeout(popupTimeoutRef.current);
          popupTimeoutRef.current = null;
        }
        if (popupRef.current && typeof popupRef.current.close === "function") {
          popupRef.current.close();
        }
      } catch (e) {
        // ignore
      } finally {
        popupRef.current = null;
      }
    };
  }, []);

  return { initiateAuth, revokeAuth, isAuthLoading };
};

export default useGoogleAuth;
